import React from "react";
import PropTypes from "prop-types";
import { Grid, List, TextField, Theme, useTheme } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { getAddressLine } from "../helpers/getAddressLine";
import { UPRN, WLR3Address } from "../types";
import { SxStyleSheet } from "../../../themes/_base";

interface FilteredListProps<TType> {
  items: TType[];
  isUprn?: boolean;
  handleItemSelected: (item: TType) => void;
  renderRow: (item: TType, index: number, onClick: () => void) => JSX.Element;
}

export function FilteredList<TType extends WLR3Address | UPRN>({
  items,
  isUprn,
  renderRow,
  handleItemSelected,
}: FilteredListProps<TType>) {
  const theme = useTheme();
  const styles = getStyles(theme);

  const [filterText, setFilterText] = React.useState("");

  const setFilter = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterText(event.target.value);
  };

  return (
    <div>
      <Grid container spacing={1} alignItems="flex-end">
        <Grid item>
          <SearchIcon />
        </Grid>
        <Grid item xs>
          <TextField
            variant="standard"
            label="Filter"
            value={filterText}
            onChange={setFilter}
            fullWidth
          />
        </Grid>
      </Grid>
      <List component="nav" sx={styles.list}>
        {items
          .filter((item) => {
            const address = isUprn
              ? (item as UPRN).address
              : getAddressLine(item as WLR3Address);
            return (
              !filterText ||
              address.toLowerCase().indexOf(filterText.toLowerCase()) !== -1
            );
          })
          .map((item, i: number) =>
            renderRow(item, i, () => {
              handleItemSelected(item);
              setFilterText("");
            })
          )}
      </List>
    </div>
  );
}

FilteredList.propTypes = {
  items: PropTypes.array.isRequired,
  isUprn: PropTypes.bool,
  renderRow: PropTypes.func.isRequired, // signature: function(object: item, int: index, func: onClick)
  handleItemSelected: PropTypes.func.isRequired, // signature: function(object: item)
};

const getStyles = (theme: Theme): SxStyleSheet => ({
  list: {
    maxHeight: 300,
    overflowY: "scroll",
    marginTop: theme.spacing(1),
  },
});
